// src/services/itemsApi.js
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseURL } from "services/api";
import { parseQueryParams } from "util/parseQueryParams";
import {
  AvailabilityRequest,
  BookableDatesResponse,
  BookableExperienceResponse,
  BookableExperiencesAvailabilityRequest,
  BookableExperiencesAvailabilityResponse,
  CreateItemWithDetailDto,
  Item,
  itemAvailabilityRequest,
  itemBookableRequest,
  ItemsByMonthResponse,
  ItemsRequest,
  UpdateBasicItemDto,
} from "./types";

const baseQuery = fetchBaseQuery({
  baseUrl: `${baseURL}/item`,
  prepareHeaders: (headers) => {
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

export const itemsApi = createApi({
  reducerPath: "itemsService",
  baseQuery,
  tagTypes: ["ItemList", "Item"], // Added "Item" for invalidation
  endpoints: (builder: EndpointBuilder<any, any, any>) => ({
    getItemsByBusinessTypeId: builder.query<any, ItemsRequest>({
      query: (ItemsRequest: ItemsRequest) => ({
        url: `/items?businessId=${ItemsRequest.id}`,
        method: "GET",
      }),
    }),

    createItem: builder.mutation<any, Partial<CreateItemWithDetailDto>>({
      query: (newItem) => ({
        url: `/`,
        method: "POST",
        body: newItem,
      }),
      invalidatesTags: (result) => [
        "ItemList",
        { type: "Item", id: result.id }, // Invalidate the specific item details
      ],
    }),

    upadateItem: builder.mutation<
      any,
      { id: number; data: Partial<CreateItemWithDetailDto> }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "ItemList",
        { type: "Item", id }, // Invalidate the specific item details
      ],
    }),

    upadateBasicItem: builder.mutation<
      any,
      { id: number; data: Partial<UpdateBasicItemDto> }
    >({
      query: ({ id, data }) => ({
        url: `simple-update?id=${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "ItemList",
        { type: "Item", id }, // Invalidate the specific item details
      ],
    }),

    getItemsByRegionAndDate: builder.query<any, itemAvailabilityRequest>({
      query: ({
        region,
        date,
        userId,
        favoriteOnly,
      }: itemAvailabilityRequest) => {
        let endpoint = "";
        if (favoriteOnly && userId) {
          endpoint = `/favorites/region/${region}/date/${date}?user_id=${userId}`;
        } else {
          endpoint = `/region/${region}/date/${date}`;
        }
        return {
          url: endpoint,
          method: "GET",
        };
      },
    }),

    getBookableDatesByRegion: builder.query<any, itemBookableRequest>({
      query: ({ region }: itemBookableRequest) => ({
        url: `/bookableDates/region/${region}`,
        method: "GET",
      }),
    }),

    getBookableDates: builder.query<BookableDatesResponse, AvailabilityRequest>(
      {
        query: (availabilityRequest: AvailabilityRequest) => ({
          url: `/items/bookableDates?${parseQueryParams(availabilityRequest)}`,
          method: "GET",
        }),
      },
    ),

    getItemDetails: builder.query<Item, number>({
      query: (id: number) => ({
        url: `/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Item", id }], // Provide a tag for specific item by id
    }),

    getItemsByBusiness: builder.query<Item[], number>({
      query: (businessId: number) => ({
        url: `/business/${businessId}`,
        method: "GET",
      }),
    }),

    deleteItem: builder.mutation<any, number>({
      query: (id: number) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),

    getBookableExperienceAvailability: builder.query<
      BookableExperiencesAvailabilityResponse,
      BookableExperiencesAvailabilityRequest
    >({
      query: (availabilityRequest: AvailabilityRequest) => ({
        url: `/bookableExperience/availability?${parseQueryParams(
          availabilityRequest,
        )}`,
        method: "GET",
      }),
    }),

    getBookableExperienceById: builder.query<
      BookableExperienceResponse,
      string
    >({
      query: (id: string) => ({
        url: `/${id}`,
        method: "GET",
      }),
    }),

    getMyItemsAvailabilities: builder.query({
      query: ({ businessId, date }) => ({
        url: `/business/${businessId}/date/${date}`,
        method: "GET",
      }),
    }),

    getBusinessItemsBookableAvailabilities: builder.query<any, any>({
      query: ({ businessId }) => ({
        url: `/business/${businessId}/bookableDates`,
        method: "GET",
      }),
    }),

    getMyMarkedDatesByItemName: builder.query({
      query: ({ name, startDate }) => ({
        url: `/name/${name}/date/${startDate}`,
        method: "GET",
      }),
    }),

    getMyItemsAvailableByDate: builder.query({
      query: ({ businessId, startDate }) => ({
        url: `/categories/business/${businessId}/date/${startDate}`,
        method: "GET",
      }),
    }),

    getBusinessItemsForMonth: builder.query<ItemsByMonthResponse, any>({
      query: ({ businessId, startDate }) => ({
        url: `/bulk/business/${businessId}/date/${startDate}`,
        method: "GET",
      }),
    }),

    getMyItemsNames: builder.query({
      query: ({ businessId, startDate }) => ({
        url: `/name/business/${businessId}/date/${startDate}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetMyItemsAvailabilitiesQuery,
  useLazyGetMyItemsAvailabilitiesQuery,
  useGetBusinessItemsBookableAvailabilitiesQuery,
  useLazyGetMyMarkedDatesByItemNameQuery,
  useGetItemsByBusinessTypeIdQuery,
  useLazyGetBookableExperienceAvailabilityQuery,
  useLazyGetItemsByRegionAndDateQuery,
  useLazyGetBookableDatesByRegionQuery,
  useLazyGetBookableDatesQuery,
  useGetBookableExperienceAvailabilityQuery,
  useGetBookableExperienceByIdQuery,
  useCreateItemMutation,
  useUpadateItemMutation,
  useUpadateBasicItemMutation,
  useLazyGetItemDetailsQuery,
  useGetItemsByBusinessQuery,
  useDeleteItemMutation,
  useLazyGetMyItemsNamesQuery,
  useLazyGetMyItemsAvailableByDateQuery,
  useLazyGetBusinessItemsForMonthQuery,
  useGetBusinessItemsForMonthQuery,
} = itemsApi;

